import { http, HttpResponse } from 'msw'

import { DEFAULT_COUNTRY } from '@ngb-frontend/shared/config'
import {
  mockFetchBooking as mockBooking,
  mockContract,
  mockDriver,
  mockVehicleObject,
  mockFetchBooking,
  mockServiceHistoryRes,
  mockLeaseChanges,
  mockVehicleDetailsRes,
  mockVehicleListRes,
  mockBookingHistory,
  msKeys,
  mockSuppliersResponse,
  mockVehicle,
  mockCountrySettings,
  fallbackTranslations,
} from '@ngb-frontend/shared/mocks'
import {
  type Booking,
  type ClientConfig,
  type Contract,
  type Driver,
  type LeaseChange,
  type ServiceHistory,
  type Suppliers,
  type VehicleObject,
  type Search,
  type Vehicle,
  type CountrySetting,
  type SitecoreContent,
  type TContentMap,
  SCBrand,
} from '@ngb-frontend/shared/types'

import { getTestServerConfig } from '../config/getTestServerConfig'

const {
  sfBase,
  sfBaseALD,
  sitecoreBase,
  denodo,
  fleetopsContractManagementBase,
  fleetopsLeaseObjectBase,
} = getTestServerConfig()

export const createApiMockHandlers = (config: ClientConfig) => [
  http.get(config.endpoints.azurePublicKeys, () => {
    return HttpResponse.json(msKeys)
  }),
  http.post(config.apiRoutes.leaseChanges, () => {
    return HttpResponse.json<LeaseChange[]>(mockLeaseChanges)
  }),
  http.post(config.apiRoutes.vehicle, () => {
    return HttpResponse.json<Vehicle>(mockVehicle)
  }),
  http.post(`${config.apiRoutes.supplierSearch}`, () => {
    return HttpResponse.json<Suppliers>(mockSuppliersResponse)
  }),
  http.post(`${config.apiRoutes.driver}`, () => {
    return HttpResponse.json<Driver>(mockDriver)
  }),
  http.post(`${config.apiRoutes.content}`, () => {
    return HttpResponse.json<Partial<TContentMap>>({
      [DEFAULT_COUNTRY]: fallbackTranslations,
    })
  }),
  http.post(`${config.apiRoutes.serviceHistory}`, () => {
    return HttpResponse.json<ServiceHistory>(mockServiceHistoryRes)
  }),
  http.post(`${config.apiRoutes.countrySettings}`, () => {
    return HttpResponse.json<CountrySetting[]>(mockCountrySettings)
  }),
  // The * ensures we catch all endpoints (some from ald have ...base/booking added)
  http.post<Booking[] | Pick<Booking, 'id' | 'global_reference_id'>>(
    `${config.apiRoutes.booking}*`,
    ({ request }) => {
      const url = new URL(request.url)

      switch (url.searchParams.get('action')) {
        case 'create': {
          return HttpResponse.json<Pick<Booking, 'id' | 'global_reference_id'>>(
            {
              id: mockBooking[0].id,
              global_reference_id: mockBooking[0].global_reference_id,
            },
          )
        }
        case 'fetchHistory': {
          return HttpResponse.json<Booking[]>(mockBookingHistory)
        }
        case 'update': {
          return HttpResponse.text('')
        }
        default: {
          return HttpResponse.json<Booking[]>(mockFetchBooking)
        }
      }
    },
  ),
  http.get(
    `${fleetopsContractManagementBase}/${config.endpoints.contract}/search/*`,
    () => {
      return HttpResponse.json<Partial<Search<Contract>>>({
        items: [mockContract as Contract],
      })
    },
  ),
  http.get(
    `${fleetopsContractManagementBase}/${config.endpoints.contract}/*/lease-changes`,
    () => {
      return HttpResponse.json(mockLeaseChanges)
    },
  ),
  http.get(`${sfBase}/${config.endpoints.driver}/*`, () => {
    return HttpResponse.json<Driver>(mockDriver)
  }),
  http.get(`${sfBaseALD}/${config.endpoints.countrySettings}/*`, () => {
    return HttpResponse.json<CountrySetting[]>(mockCountrySettings)
  }),
  http.get(`${sitecoreBase}/*`, () => {
    return HttpResponse.json<Partial<SitecoreContent>>({
      [SCBrand.Ayvens]: fallbackTranslations,
    })
  }),
  http.post(`${sfBase}/${config.endpoints.supplierSearch}`, () => {
    return HttpResponse.json<Suppliers>(mockSuppliersResponse)
  }),
  // The * ensures we catch all endpoints (some from ald have ...base/booking added)
  http.get<Booking | Booking[]>(`${sfBase}*`, ({ request }) => {
    const url = new URL(request.url)

    // fetch booking (when id is provided)
    if (url.searchParams.get('id')) {
      return HttpResponse.json<Booking>(mockBooking[0])
    }

    // else ilan or registration is provided and we get a list
    return HttpResponse.json<Booking[]>(mockBooking)
  }),
  // create booking
  // The * ensures we catch all endpoints (some from ald have ...base/booking added)
  http.post(`${sfBase}*`, () => {
    return HttpResponse.json<Pick<Booking, 'id' | 'global_reference_id'>>({
      id: mockBooking[0].id,
      global_reference_id: mockBooking[0].global_reference_id,
    })
  }),
  // update booking
  http.put(`${sfBase}/${config.endpoints.booking}/*`, () => {
    return HttpResponse.text('')
  }),
  http.get(
    `${fleetopsLeaseObjectBase}/${config.endpoints.vehicleObject}/*`,
    () => {
      return HttpResponse.json<VehicleObject>(
        mockVehicleObject as VehicleObject,
      )
    },
  ),
  http.get(`${denodo.base}/${config.endpoints.serviceHistory}`, () => {
    return HttpResponse.json<ServiceHistory>(mockServiceHistoryRes)
  }),
  http.get(`${denodo.base}/${config.endpoints.vehicleList}`, () => {
    return HttpResponse.json(mockVehicleListRes)
  }),
  http.get(`${denodo.base}/${config.endpoints.vehicleDetails}`, () => {
    return HttpResponse.json(mockVehicleDetailsRes)
  }),
  http.all('*datadog*', () => {
    return HttpResponse.json({ mocked: true })
  }),
]
